import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate } from "react-router-dom";
import { subscriptionService } from "../../api";

// Reflects GET /subscription/outstanding-status:
//  - outstandingAmount > 0            -> persistent info banner (verbatim message)
//  - isDisabledForNonPayment === true -> full-screen block
export default function OutstandingStatusBanner() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);

  const fetchStatus = useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    const role = localStorage.getItem("role");
    // Only Admins own a subscription/billing relationship.
    if (!token || role !== "Admin") {
      setStatus(null);
      return;
    }
    try {
      const res = await subscriptionService.outstandingStatus();
      if (res?.success !== false) setStatus(res?.data || null);
    } catch (e) {
      /* leave prior state */
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    window.addEventListener("subscription_updated", fetchStatus);
    return () => window.removeEventListener("subscription_updated", fetchStatus);
  }, [fetchStatus]);

  if (!status) return null;

  const isDisabled = status.isDisabledForNonPayment === true;
  const hasOutstanding = Number(status.outstandingAmount) > 0;

  // ---- Full block screen (account disabled for non-payment) ----------------
  if (isDisabled) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 2000,
          backgroundColor: "rgba(20,20,20,0.92)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "16px",
            maxWidth: 460,
            width: "100%",
            p: 4,
            textAlign: "center",
          }}
        >
          <WarningAmberIcon sx={{ color: "#d32f2f", fontSize: 48, mb: 1 }} />
          <Typography sx={{ fontSize: "20px", fontWeight: 700, mb: 1 }}>
            Account access suspended
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#555", mb: 3 }}>
            {status.message ||
              "Your account has been disabled due to an outstanding subscription balance."}
          </Typography>
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate("/planManagement")}
            sx={{ textTransform: "none", backgroundColor: "#000", mb: 1 }}
          >
            Manage plan &amp; settle balance
          </Button>
          <Button
            fullWidth
            onClick={() => {
              localStorage.clear();
              navigate("/");
            }}
            sx={{ textTransform: "none", color: "#878787" }}
          >
            Sign out
          </Button>
        </Box>
      </Box>
    );
  }

  // ---- Persistent outstanding-balance banner -------------------------------
  if (!hasOutstanding) return null;

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1200,
        backgroundColor: "#FBEBD6",
        border: "1px solid #f0c98a",
        borderRadius: "8px",
        px: 2,
        py: 1.25,
        mb: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <WarningAmberIcon sx={{ color: "#b26a00" }} fontSize="small" />
      <Typography sx={{ flex: 1, fontSize: "13px", color: "#5a3c00" }}>
        {status.message}
      </Typography>
    </Box>
  );
}
